import React from 'react';
import AutocompleteTextField from './AutocompleteTextField';

/**
 * TextFieldWithSuggestions - A wrapper component that conditionally renders
 * either AutocompleteTextField (when suggestions are enabled) or a regular input
 * 
 * This component checks the entity config to determine if the field should
 * show autocomplete suggestions from existing data.
 * 
 * @param {object} props
 * @param {string} props.entityName - Entity name (e.g., 'lead', 'customer')
 * @param {string} props.fieldName - Field name in the entity
 * @param {object} props.entityConfig - The entity configuration object
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.className - CSS classes for the input
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.id - Input element ID
 * @param {string} props.name - Input element name
 */
const TextFieldWithSuggestions = ({
  entityName,
  fieldName,
  entityConfig,
  value = '',
  onChange,
  placeholder = '',
  className = '',
  disabled = false,
  required = false,
  id,
  name,
  ...restProps
}) => {
  // Check if field has suggestFromData enabled in config
  const fieldConfig = entityConfig?.fields?.[fieldName];
  const suggestConfig = fieldConfig?.ui?.suggestFromData;
  const suggestEnabled = suggestConfig?.enabled === true;

  // Wrap onChange to work with AutocompleteTextField's event format
  const handleChange = (e) => {
    // If it's a string (from custom components), create event-like object
    if (typeof e === 'string') {
      onChange({ target: { name: name || fieldName, value: e } });
    } else if (e && e.target) {
      // Standard event
      onChange(e);
    }
  };

  if (suggestEnabled) {
    return (
      <AutocompleteTextField
        entityName={entityName}
        fieldName={fieldName}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        required={required}
        id={id || fieldName}
        name={name || fieldName}
        suggestConfig={{
          enabled: true,
          minChars: suggestConfig?.minChars || 0,
          maxSuggestions: suggestConfig?.maxSuggestions || 20
        }}
        {...restProps}
      />
    );
  }

  // Regular text input
  return (
    <input
      type="text"
      id={id || fieldName}
      name={name || fieldName}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      required={required}
      {...restProps}
    />
  );
};

/**
 * Helper function to check if a field should use autocomplete suggestions
 * @param {object} entityConfig - Entity configuration
 * @param {string} fieldName - Field name
 * @returns {boolean}
 */
export const shouldUseSuggestions = (entityConfig, fieldName) => {
  const fieldConfig = entityConfig?.fields?.[fieldName];
  return fieldConfig?.ui?.suggestFromData?.enabled === true;
};

/**
 * Helper function to get suggestion config for a field
 * @param {object} entityConfig - Entity configuration
 * @param {string} fieldName - Field name
 * @returns {object|null}
 */
export const getSuggestionConfig = (entityConfig, fieldName) => {
  const fieldConfig = entityConfig?.fields?.[fieldName];
  if (!fieldConfig?.ui?.suggestFromData?.enabled) return null;
  return {
    enabled: true,
    minChars: fieldConfig.ui.suggestFromData.minChars || 0,
    maxSuggestions: fieldConfig.ui.suggestFromData.maxSuggestions || 20,
    cacheMinutes: fieldConfig.ui.suggestFromData.cacheMinutes || 5
  };
};

export default TextFieldWithSuggestions;
